import {createTheme, ThemeProvider} from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import './App.css'
import {BrowserRouter, Route, Routes} from 'react-router-dom'
import {AuthProvider} from './components/auth/AuthProvider'
import MainLayout from './MainLayout'
import Home from './pages/Home'
import Login from './pages/auth/Login'
import MyDecks from './pages/MyDecks'
import CreateAndEditDeck from './pages/CreateAndEditDeck'
import CardPage from './pages/CardPage'
import Logout from './pages/auth/Logout'
import CreateAndEditCard from './pages/CreateAndEditCard'
import RequireNotAuth from './components/auth/RequireNotAuth'
import RequireAuth from './components/auth/RequireAuth'
import DeckPage from './pages/DeckPage'
import NotFound from './pages/NotFound'
import Register from './pages/auth/Register'
import RegisterNext from './pages/auth/RegisterNext'
import RegisterResendEmail from './pages/auth/RegisterResendEmail'
import VerifyEmail from './pages/auth/VerifyEmail'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import ScrollToTop from './components/ScrollToTop'
import Play from './pages/Play'
import type {} from '@mui/x-data-grid/themeAugmentation'
import UISandbox from './pages/UISandbox'
import GoogleAnalytics from './components/GoogleAnalytics'
import {SnackbarProvider} from 'notistack'
import ErrorPage from './pages/ErrorPage'
import PublicDecks from './pages/PublicDecks'
import ToS from './pages/TermsOfService'
import SignUp from './pages/auth/SignUp'
import AdminLayout from './AdminLayout'
import AdminHome from './pages/admin/AdminHome'
import Users from './pages/admin/Users'
import ContentPolicy from './pages/ContentPolicy'
import Whitelist from './pages/admin/Whitelist'
import DeckRedirect from './pages/redirect/DeckRedirect'
import HomeRedirect from './pages/redirect/HomeRedirect'
import PublicDecksRedirect from './pages/redirect/PublicDecksRedirect'
import MarkdownHelp from './pages/MarkdownHelp'
import Faq from './pages/Faq'
import ServiceWorkerWrapper from './components/ServiceWorkerWrapper'
import React from 'react'
import LoginHelp from './pages/auth/LoginHelp'
import ResetPassword from './pages/ResetPassword'
import Feedback from './pages/Feedback'
import FeedbackAdmin from './pages/admin/FeedbackAdmin'

function App() {
  const theme = createTheme({
    components: {
      // Use `MuiDataGrid` on both DataGrid and DataGridPro
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: 'dimgrey',
          },
        },
      },
    },

    palette: {
      mode: 'dark'
    },
    typography: {
      fontFamily: [
        'Montserrat',
        'sans-serif'
      ].join(','),
    }
  })

  theme.typography.h3 = {
    fontSize: '1.2rem',
    '@media (min-width:600px)': {
      fontSize: '1.5rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
    },
  }

  theme.typography.h1 = {
    fontSize: '4rem',

    [theme.breakpoints.up('md')]: {
      fontSize: '4rem',
    },
  }

  theme.typography.h2 = {
    fontSize: '2rem',
    '@media (min-width:600px)': {
      fontSize: '2rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
    },
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline/>
      <BrowserRouter>
        <ServiceWorkerWrapper/>
        <GoogleAnalytics>
          <SnackbarProvider maxSnack={3} anchorOrigin={{vertical: 'bottom', horizontal: 'left',}} autoHideDuration={1500}>
            <ScrollToTop>
              <AuthProvider>
                <Routes>
                  <Route path="/" element={<MainLayout/>}>
                    <Route index element={<Home/>}/>
                    <Route path="/deck/:id" element={<DeckPage publicDeckView={false}/>}/>
                    <Route path="/deck/public/:id" element={<DeckPage publicDeckView={true}/>}/>
                    <Route path="/my-decks" element={<RequireAuth><MyDecks/></RequireAuth>}/>
                    <Route path="/new-deck" element={<RequireAuth><CreateAndEditDeck/></RequireAuth>}/>
                    <Route path="/edit-deck/:deckIdParam" element={<RequireAuth><CreateAndEditDeck/></RequireAuth>}/>
                    <Route path="/card" element={<RequireAuth><CardPage/></RequireAuth>}/>
                    <Route path="/deck/:deckIdParam/new-card" element={<CreateAndEditCard/>}/>
                    <Route path="/deck/:deckIdParam/edit-card/:cardIdParam" element={<CreateAndEditCard/>}/>
                    <Route path="/login" element={<RequireNotAuth><Login/></RequireNotAuth>}/>
                    <Route path="/sign-up" element={<RequireNotAuth><SignUp/></RequireNotAuth>}/>
                    <Route path="/register" element={<RequireNotAuth><Register/></RequireNotAuth>}/>
                    <Route path="/verify-email" element={<VerifyEmail/>}/>
                    <Route path="/register-next" element={<RequireNotAuth><RegisterNext/></RequireNotAuth>}/>
                    <Route path="/register-resend-email" element={<RequireNotAuth><RegisterResendEmail/></RequireNotAuth>}/>
                    <Route path="/forgot-password" element={<LoginHelp/>}/>
                    <Route path="/reset-password" element={<ResetPassword/>}/>
                    <Route path="/privacy" element={<Privacy/>}/>
                    <Route path="/terms" element={<Terms/>}/>
                    <Route path="/content" element={<ContentPolicy/>}/>
                    <Route path="/feedback" element={<Feedback/>}/>
                    <Route path="/ui-sandbox" element={<UISandbox/>}/>
                    <Route path="/error" element={<ErrorPage/>}/>
                    <Route path="/terms-of-service" element={<ToS/>}/>
                    <Route path="/public-decks" element={<PublicDecks/>}/>
                    <Route path="/markdown-help" element={<MarkdownHelp/>}/>
                    <Route path="/faq" element={<Faq/>}/>
                    <Route path="*" element={<NotFound/>}/>
                  </Route>
                  <Route path="/admin" element={<RequireAuth role="SUPER_ADMIN"><AdminLayout/></RequireAuth>}>
                    <Route index element={<AdminHome/>}/>
                    <Route path="/admin/users" element={<Users/>}/>
                    <Route path="/admin/whitelist" element={<Whitelist/>}/>
                    <Route path="/admin/feedback" element={<FeedbackAdmin/>}/>
                    <Route path="*" element={<NotFound/>}/>
                  </Route>
                  <Route path="/play/:deckIdParam" element={<RequireAuth><Play/></RequireAuth>}/>
                  <Route path="/logout" element={<Logout/>}/>
                  <Route path="/redirect/deck" element={<DeckRedirect/>}/>
                  <Route path="/redirect/home" element={<HomeRedirect/>}/>
                  <Route path="/redirect/public-decks" element={<PublicDecksRedirect/>}/>
                </Routes>
              </AuthProvider>
            </ScrollToTop>
          </SnackbarProvider>
        </GoogleAnalytics>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
